import { create } from 'zustand';
import { ChatRoom, ChatMessage, TypingUser } from '../types/chat.types';

interface ChatStore {
  addMessage: (roomId: string, message: ChatMessage) => void;
  addTypingUser: (roomId: string, user: TypingUser) => void;
  clearUnread: (roomId: string) => void;
  currentRoom: ChatRoom | null;
  deleteMessage: (roomId: string, messageId: string) => void;
  incrementUnread: (roomId: string) => void;
  messages: Record<string, ChatMessage[]>;
  onlineUsers: Set<string>;
  removeTypingUser: (roomId: string, userId: string) => void;
  rooms: ChatRoom[];
  setCurrentRoom: (room: ChatRoom | null) => void;
  setMessages: (roomId: string, messages: ChatMessage[]) => void;
  setRooms: (rooms: ChatRoom[]) => void;
  setUserOffline: (userId: string) => void;
  setUserOnline: (userId: string) => void;
  typingUsers: Record<string, TypingUser[]>;
  unreadCounts: Record<string, number>;
  updateMessage: (
    roomId: string,
    messageId: string,
    updates: Partial<ChatMessage>
  ) => void;
  updateRoomLastMessage: (roomId: string, message: ChatMessage) => void;
}

export const useChatStore = create<ChatStore>((set) => ({
  addMessage: (roomId, message) =>
    set((state) => ({
      messages: {
        ...state.messages,
        [roomId]: [...(state.messages[roomId] || []), message]
      }
    })),

  addTypingUser: (roomId, user) =>
    set((state) => {
      const currentTyping = state.typingUsers[roomId] || [];
      const exists = currentTyping.some((u) => u.userId === user.userId);

      if (exists) return state;

      return {
        typingUsers: {
          ...state.typingUsers,
          [roomId]: [...currentTyping, user]
        }
      };
    }),

  clearUnread: (roomId) =>
    set((state) => {
      console.log('🧹 clearUnread called for:', roomId);

      // FIXED: Create NEW array with NEW room objects
      const updatedRooms = state.rooms.map((room) =>
        room.id === roomId
          ? { ...room, unreadCount: 0 } // New object!
          : room
      );

      return {
        rooms: updatedRooms, // New array reference!
        unreadCounts: {
          ...state.unreadCounts,
          [roomId]: 0
        }
      };
    }),

  currentRoom: null,

  deleteMessage: (roomId, messageId) =>
    set((state) => ({
      messages: {
        ...state.messages,
        [roomId]: state.messages[roomId]?.filter((msg) => msg.id !== messageId)
      }
    })),

  incrementUnread: (roomId) =>
    set((state) => {
      const newCount = (state.unreadCounts[roomId] || 0) + 1;
      console.log(
        '🔔 incrementUnread called for:',
        roomId,
        'new count:',
        newCount
      );

      // FIXED: Create NEW array with NEW room objects
      const updatedRooms = state.rooms.map((room) =>
        room.id === roomId
          ? { ...room, unreadCount: newCount } // New object!
          : room
      );

      console.log(
        '🔔 Updated rooms array created, length:',
        updatedRooms.length
      );

      return {
        rooms: updatedRooms, // New array reference!
        unreadCounts: {
          ...state.unreadCounts,
          [roomId]: newCount
        }
      };
    }),

  messages: {},

  onlineUsers: new Set(),

  removeTypingUser: (roomId, userId) =>
    set((state) => ({
      typingUsers: {
        ...state.typingUsers,
        [roomId]: (state.typingUsers[roomId] || []).filter(
          (u) => u.userId !== userId
        )
      }
    })),

  rooms: [],

  setCurrentRoom: (room) => {
    console.log('📍 setCurrentRoom:', room?.id || 'null');
    set({ currentRoom: room });
  },

  setMessages: (roomId, messages) => {
    console.log(
      '📝 setMessages called for room:',
      roomId,
      'with',
      messages.length,
      'messages'
    );
    set((state) => {
      const newState = {
        messages: {
          ...state.messages,
          [roomId]: messages
        }
      };
      console.log(
        '📝 New messages state:',
        Object.keys(newState.messages).map((id) => ({
          count: newState.messages[id].length,
          roomId: id
        }))
      );
      return newState;
    });
  },

  setRooms: (rooms) => {
    console.log('🏠 setRooms called with', rooms.length, 'rooms');
    set({ rooms });
  },

  setUserOffline: (userId) =>
    set((state) => {
      const newSet = new Set(state.onlineUsers);
      newSet.delete(userId);
      return { onlineUsers: newSet };
    }),

  setUserOnline: (userId) =>
    set((state) => ({
      onlineUsers: new Set(Array.from(state.onlineUsers).concat(userId))
    })),

  typingUsers: {},

  unreadCounts: {},

  updateMessage: (roomId, messageId, updates) =>
    set((state) => ({
      messages: {
        ...state.messages,
        [roomId]: state.messages[roomId]?.map((msg) =>
          msg.id === messageId ? { ...msg, ...updates } : msg
        )
      }
    })),

  updateRoomLastMessage: (roomId, message) =>
    set((state) => {
      console.log(
        '📝 updateRoomLastMessage called for:',
        roomId,
        'message:',
        message.messageText
      );
      console.log('📝 Current rooms count:', state.rooms.length);

      // FIXED: Create NEW array with NEW room objects
      const updatedRooms = state.rooms.map((room) =>
        room.id === roomId
          ? { ...room, lastMessage: message } // New object!
          : room
      );

      console.log(
        '📝 Updated rooms array created, length:',
        updatedRooms.length
      );
      return { rooms: updatedRooms }; // New array reference!
    })
}));

export default useChatStore;
